import { useEffectOnce } from '@carvana/merch-react-hooks';
import { datadogRum } from '@datadog/browser-rum';
import { useRef } from 'react';
import { DeviceSource } from '~/constants/common/DeviceSource';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useDeviceType } from '~/context/common/DeviceContext';
import { useShowComposerTreatment } from '~/context/composer/ComposerContext';
import { logInfo } from '~/utilities/loggers';

export default function useTrackServerClientMobileMismatch() {
  const deviceType = useDeviceType();
  const showComposerTreatment = useShowComposerTreatment();
  // Capture the SSR-derived value before useResizeListener updates DeviceContext
  const serverIsMobileRef = useRef(deviceType.isMobile);
  const trackEvent = useTrackEvent();

  useEffectOnce(
    () => {
      if (deviceType.isMobile === serverIsMobileRef.current) return;

      const context = {
        isMobile: {
          browser: deviceType.isMobile,
          server: serverIsMobileRef.current
        }
      };

      trackEvent('Server vs client isMobile mismatch', context);
      logInfo('Server vs client isMobile mismatch', context);
    },
    [deviceType.isMobile, trackEvent],
    deviceType.source === DeviceSource.Browser
  );

  // showComposerTreatment already implies the server saw isMobile: true — this only
  // fires for the critical case where the confirmed browser value disagrees, meaning a
  // real user is currently looking at the composer treatment on a non-mobile device.
  useEffectOnce(
    () => {
      if (!showComposerTreatment || deviceType.isMobile) return;

      const context = {
        isMobile: {
          browser: deviceType.isMobile,
          server: serverIsMobileRef.current
        },
        showComposerTreatment
      };

      datadogRum.addAction(
        'Composer treatment shown on non-mobile device',
        context
      );
      trackEvent('Composer treatment shown on non-mobile device', context);
    },
    [deviceType.isMobile, showComposerTreatment, trackEvent],
    deviceType.source === DeviceSource.Browser
  );
}
